import React from "react";
import { Link } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { utils, writeFile } from "xlsx-js-style";
import ServiceColumn, {
  ServiceBackground,
  ServiceConact,
} from "./ServiceColumn";
import { formatName, formatNumberWithCommas } from "../../utils";
import { getOrderType, getReason } from "../../models/ReportOperatorSalary";

/**
 * Calcula la comisión y devuelve el objeto con porcentaje y monto
 */
const calculateCommissionBySubtotal = (subtotal, priceRanges) => {
  if (!subtotal || subtotal <= 0 || !Array.isArray(priceRanges))
    return { commission: 0, percentage: 0 };

  const range = priceRanges.find(
    (r) => subtotal >= r.minimo && (r.maximo === null || subtotal <= r.maximo)
  );

  if (!range) return { commission: 0, percentage: 0 };

  const commission = (subtotal * range.porcentaje) / 100;
  return {
    commission: parseFloat(commission.toFixed(2)),
    percentage: range.porcentaje,
  };
};

const TableOperatorCommissionBySales = ({ orders, priceRanges }) => {
  const baseUrl = window.location.origin;

  // Función para exportar el reporte individual de un operador
  const exportOperatorToExcel = (operatorName, operatorOrders) => {
    const workbook = utils.book_new();

    const sheetName = operatorName.substring(0, 30);

    const sheetData = [
      [
        "Folio",
        "Descripción del servicio",
        "Tipo de Servicio",
        "Fecha",
        "Total (Sin IVA)",
        "KM aproximados",
        "Grúa",
        "Tipo de grúa",
        "Razón",
      ],
    ];

    let totalSubtotal = 0;
    let totalDistanceSum = 0;

    operatorOrders.forEach((order) => {
      const orderType =
        order.tipoServicio != null ? order.tipoServicio : getOrderType(order);

      const bg = ServiceBackground({ row: order });

      const reason = order.razon?.trim() ? order.razon : getReason(order);

      const subtotal = order.orden_en_cero ? 0 : Number(order.subtotal) || 0;

      totalSubtotal += subtotal;
      totalDistanceSum += Number(order.totalDistance) || 0;

      sheetData.push([
        {
          t: "s",
          v: order.folio.toString().padStart(5, "0"),
          l: {
            Target:
              order.tipo === "Varo Express"
                ? `${baseUrl}/orders-vial/editOrder/${order.id}`
                : `${baseUrl}/orders/edit/${order.id}`,
          },
          s: { fill: { fgColor: { rgb: bg } } },
        },
        {
          t: "s",
          v: orderType,
          s: { fill: { fgColor: { rgb: bg } } },
        },
        {
          t: "s",
          v: ServiceConact({ row: order }),
          s: { fill: { fgColor: { rgb: bg } } },
        },
        {
          t: "s",
          v: format(new Date(order.fecha_hora_reporte), "dd/MMM/yyyy", {
            locale: es,
          }),
          s: { fill: { fgColor: { rgb: bg } } },
        },
        {
          t: "n",
          v: subtotal,
          s: { fill: { fgColor: { rgb: bg } } },
        },
        {
          t: "n",
          v: Number(order.totalDistance || 0).toFixed(2),
          s: { fill: { fgColor: { rgb: bg } } },
        },
        {
          t: "s",
          v: order?.Crane?.description || "",
          s: { fill: { fgColor: { rgb: bg } } },
        },
        {
          t: "s",
          v: order?.Crane?.CraneTypes?.at(0)?.crane_type_name || "",
          s: { fill: { fgColor: { rgb: bg } } },
        },
        {
          t: "s",
          v: reason,
          s: { fill: { fgColor: { rgb: bg } } },
        },
      ]);
    });

    // Calcular comisión total y porcentaje según el subtotal acumulado
    const { commission, percentage } = calculateCommissionBySubtotal(
      totalSubtotal,
      priceRanges
    );

    sheetData.push([{ t: "s", v: "" }]); // línea vacía

    sheetData.push([
      { t: "s", v: "Total" },
      { t: "s", v: "" },
      { t: "s", v: "" },
      { t: "s", v: "" },
      { t: "n", v: totalSubtotal.toFixed(2) },
      { t: "n", v: totalDistanceSum.toFixed(2) },
      {
        t: "s",
        v: `Comisión ${percentage}%` + " " + "(" + commission.toFixed(2) + ")",
        s: {
          alignment: { horizontal: "left" },
          fill: { fgColor: { rgb: "FFF3CD" } },
          font: { bold: true, color: { rgb: "856404" } },
          border: {
            top: { style: "thin", color: { rgb: "FFC107" } },
            bottom: { style: "thin", color: { rgb: "FFC107" } },
            left: { style: "thin", color: { rgb: "FFC107" } },
            right: { style: "thin", color: { rgb: "FFC107" } },
          },
        },
      },
      { t: "s", v: "" },
      { t: "s", v: "" },
    ]);

    const worksheet = utils.aoa_to_sheet(sheetData);
    worksheet["!cols"] = [
      { wch: 20 },
      { wch: 35 },
      { wch: 20 },
      { wch: 30 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 35 },
      { wch: 35 },
    ];

    utils.book_append_sheet(workbook, worksheet, sheetName);

    const fileName = `${sheetName.replace(/[^\w\s-]/g, "")}_${format(
      new Date(),
      "yyyy-MM-dd"
    )}.xlsx`;
    writeFile(workbook, fileName);
  };

  // Agrupar órdenes por operador
  const groupedByOperator = orders.reduce((acc, order) => {
    const operatorName = order?.operador
      ? formatName(order.operador)
      : "Sin operador";
    if (!acc[operatorName]) acc[operatorName] = [];
    acc[operatorName].push(order);
    return acc;
  }, {});

  const hasData = orders && orders.length > 0;

  // Calcular altura fija para cada operador (máximo 3 filas visibles)
  // Altura aproximada por fila: ~60-70px, para 3 filas: ~210px
  const OPERATOR_CONTAINER_HEIGHT = "210px"; // Altura fija para todos los operadores

  return (
    <div className="table-responsive mb-3">
      <table
        className="table table-hover table-panel text-nowrap align-middle mb-0"
        style={{ tableLayout: "fixed", width: "100%" }}
      >
        <colgroup>
          <col style={{ width: "180px" }} />
          <col style={{ width: "100px" }} />
          <col style={{ width: "130px" }} />
          <col style={{ width: "130px" }} />
          <col style={{ width: "200px" }} />
          <col style={{ width: "120px" }} />
          <col style={{ width: "150px" }} />
          <col style={{ width: "150px" }} />
        </colgroup>
        <thead className="sticky-thead table-light text-center">
          <tr>
            <th
              className="align-middle"
              style={{
                width: "90px",
                maxWidth: "200px",
                minWidth: "90px",
                whiteSpace: "normal",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              Operador
            </th>
            <th className="align-middle" style={{ width: "100px" }}>
              Orden
            </th>
            <th className="align-middle" style={{ width: "130px" }}>
              KM aproximados
            </th>
            <th className="align-middle" style={{ width: "130px" }}>
              Total (Sin IVA)
            </th>
            <th className="align-middle" style={{ width: "200px" }}>
              Descripción del servicio
            </th>
            <th className="align-middle" style={{ width: "120px" }}>
              Fecha
            </th>
            <th className="align-middle" style={{ width: "150px" }}>
              Grúa
            </th>
            <th className="align-middle" style={{ width: "150px" }}>
              Tipo de grúa
            </th>
          </tr>
        </thead>

        <tbody>
          {!hasData ? (
            <tr>
              <td colSpan="8" className="text-center py-5">
                <div className="text-muted fs-5">
                  No hay registros disponibles por ahora
                  <br />
                  <small>
                    Intenta ajustar los filtros o verificar la información
                  </small>
                </div>
              </td>
            </tr>
          ) : (
            Object.entries(groupedByOperator).map(
              ([operatorName, operatorOrders], index) => {
                const totalSubtotal = operatorOrders.reduce(
                  (sum, o) => sum + (o.subtotal || 0),
                  0
                );

                const totalKilometros = operatorOrders.reduce(
                  (sum, o) => sum + (parseFloat(o.totalDistance) || 0),
                  0
                );

                const { commission, percentage } =
                  calculateCommissionBySubtotal(totalSubtotal, priceRanges);

                const bgClass =
                  index % 2 === 0 ? "table-primary" : "table-secondary";

                // Separar órdenes visibles (máximo 3) y las que van en scroll
                const visibleOrders = operatorOrders.slice(0, 3);
                const scrollableOrders = operatorOrders.slice(3);
                const hasScrollableOrders = scrollableOrders.length > 0;

                return (
                  <React.Fragment key={operatorName}>
                    <tr>
                      {/* Celda del operador con altura fija */}
                      <td
                        rowSpan={2}
                        className={`fw-bold align-middle text-center ${bgClass}`}
                        style={{
                          verticalAlign: "top",
                          padding: "1.5rem 0.75rem",
                          fontSize: "1rem",
                          letterSpacing: "0.5px",
                          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
                          borderRight: "2px solid rgba(0,0,0,0.1)",
                          fontWeight: "600",
                          width: "90px",
                          maxWidth: "200px",
                          minWidth: "200px",
                          color: "#000",
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          height: OPERATOR_CONTAINER_HEIGHT,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            lineHeight: "1.4",
                          }}
                        >
                          <span style={{ whiteSpace: "normal" }}>
                            {operatorName}
                          </span>
                        </div>
                      </td>

                      {/* Contenedor con scroll para las órdenes */}
                      <td
                        colSpan="7"
                        style={{
                          padding: 0,
                          height: OPERATOR_CONTAINER_HEIGHT,
                          verticalAlign: "top",
                        }}
                      >
                        <div
                          style={{
                            height: OPERATOR_CONTAINER_HEIGHT,
                            maxHeight: OPERATOR_CONTAINER_HEIGHT,
                            overflowY: "auto",
                            overflowX: "hidden",
                          }}
                        >
                          <table
                            className="table table-hover table-panel text-nowrap align-middle mb-0"
                            style={{
                              marginBottom: 0,
                              tableLayout: "fixed",
                              width: "100%",
                            }}
                          >
                            <colgroup>
                              <col style={{ width: "100px" }} />
                              <col style={{ width: "130px" }} />
                              <col style={{ width: "130px" }} />
                              <col style={{ width: "200px" }} />
                              <col style={{ width: "120px" }} />
                              <col style={{ width: "180px" }} />
                              <col style={{ width: "150px" }} />
                            </colgroup>
                            <tbody>
                              {/* Filas visibles (máximo 3) */}
                              {visibleOrders.map((order) => (
                                <tr className="text-center" key={order.id}>
                                  <td>
                                    <Row className="mb-2">
                                      <Col md="12">
                                        <Link
                                          to={
                                            order.tipo === "Varo Express"
                                              ? `/orders-vial/editOrder/${order.id}`
                                              : `/orders/edit/${order.id}`
                                          }
                                        >
                                          {order.folio
                                            ?.toString()
                                            .padStart(5, "0")}
                                        </Link>
                                      </Col>
                                    </Row>
                                  </td>
                                  <td>
                                    {!isNaN(Number(order?.totalDistance))
                                      ? `${Number(order?.totalDistance).toFixed(
                                          2
                                        )} km`
                                      : "0.00 km"}
                                  </td>
                                  <td>
                                    $
                                    {formatNumberWithCommas(
                                      order.subtotal || 0
                                    )}
                                  </td>

                                  <td>
                                    <ServiceColumn row={order} />
                                  </td>
                                  <td>
                                    {format(
                                      new Date(order.fecha_hora_reporte),
                                      "dd/MMM/yyyy",
                                      { locale: es }
                                    )}
                                  </td>
                                  <td>{order?.Crane?.description || "N/A"}</td>
                                  <td>
                                    {order?.Crane?.CraneTypes?.[0]
                                      ?.crane_type_name || "N/A"}
                                  </td>
                                </tr>
                              ))}

                              {/* Filas adicionales en scroll (si hay más de 3) */}
                              {scrollableOrders.map((order) => (
                                <tr className="text-center" key={order.id}>
                                  <td>
                                    <Row className="mb-2">
                                      <Col md="12">
                                        <Link
                                          to={
                                            order.tipo === "Varo Express"
                                              ? `/orders-vial/editOrder/${order.id}`
                                              : `/orders/edit/${order.id}`
                                          }
                                        >
                                          {order.folio
                                            ?.toString()
                                            .padStart(5, "0")}
                                        </Link>
                                      </Col>
                                    </Row>
                                  </td>
                                  <td>
                                    {!isNaN(Number(order?.totalDistance))
                                      ? `${Number(order?.totalDistance).toFixed(
                                          2
                                        )} km`
                                      : "0.00 km"}
                                  </td>
                                  <td>
                                    $
                                    {formatNumberWithCommas(
                                      order.subtotal || 0
                                    )}
                                  </td>

                                  <td>
                                    <ServiceColumn row={order} />
                                  </td>
                                  <td>
                                    {format(
                                      new Date(order.fecha_hora_reporte),
                                      "dd/MMM/yyyy",
                                      { locale: es }
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      whiteSpace: "normal",
                                      wordWrap: "break-word",
                                      overflowWrap: "break-word",
                                      wordBreak: "break-word",
                                      textAlign: "center",
                                    }}
                                  >
                                    {order?.Crane?.description || "N/A"}
                                  </td>

                                  <td>
                                    {order?.Crane?.CraneTypes?.[0]
                                      ?.crane_type_name || "N/A"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>

                    {/* Fila resumen del operador */}
                    <tr className={`${bgClass} text-center fw-bold`}>
                      <td>
                        {operatorOrders.length} orden
                        {operatorOrders.length !== 1 ? "es" : ""}
                      </td>
                      <td>{totalKilometros.toFixed(2)} km</td>

                      <td>${formatNumberWithCommas(totalSubtotal)}</td>
                      <td>
                        <span
                          style={{
                            backgroundColor: "#e7f3ff",
                            color: "#0066cc",
                            fontWeight: "bold",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            display: "inline-block",
                            border: "1px solid #b3d9ff",
                          }}
                        >
                          Comisión {percentage ? `${percentage}%` : "-"} = $
                          {formatNumberWithCommas(commission)}
                        </span>
                      </td>
                      <td></td>
                      <td>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() =>
                            exportOperatorToExcel(operatorName, operatorOrders)
                          }
                          title="Descargar reporte individual"
                        >
                          <i className="fa-solid fa-file-excel" />
                          &nbsp;Descargar
                        </button>
                      </td>
                      <td></td>
                    </tr>

                    {/* Separador visual */}
                    <tr>
                      <td
                        colSpan="8"
                        style={{ height: "10px", backgroundColor: "#f8f9fa" }}
                      ></td>
                    </tr>
                  </React.Fragment>
                );
              }
            )
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TableOperatorCommissionBySales;
